/**
 * Opportunities (Quotes List) — Cockpit skin
 * ──────────────────────────────────────────
 * Same data, same routes, same role gating, same create flow.
 * Restructured: compact masthead w/ pipeline-value hero, hairline
 * filter bar (no card), hairline opportunity list (no table chrome).
 */
import { useState, useMemo, useEffect } from "react";
import { usePagination } from "@/hooks/usePagination";
import { useProjectOpportunitiesPage, useProjectOpportunitiesSummary } from "@/hooks/useProjectServerPaging";
import { PaginationBar } from "@/components/PaginationBar";
import { useNavigate } from "react-router-dom";
import { useAppData } from "@/data/AppDataContext";
import { useRole } from "@/data/RoleContext";
import { useCurrentRep } from "@/hooks/useCurrentRep";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { supabase } from "@/lib/supabase";
import { LeadService } from "@/services/lead.service";
import { QuoteService } from "@/services/quote.service";
import { toUiQuote } from "@/types/mappers/quote.mapper";
import type { Quote, QuoteStatus } from "@/data/quoteTypes";
import { isActiveStatus } from "@/data/quoteTypes";
import { getAllOpportunities, getOpportunityStats } from "@/data/opportunitySelectors";
import { formatShortDate } from "@/lib/dateFormat";
import { PROJECT_TYPES, type Job, type ProjectType } from "@/data/types";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { formatPhoneNumber } from "@/lib/phoneFormat";
import { Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { useActionPermissions } from "@/hooks/useActionPermissions";
import { toast } from "sonner";
import {
  CockpitPage, CockpitMasthead, HeroMetric, HeroDivider,
  CockpitTabs, CockpitSearch, CockpitListShell, CockpitColumns, CockpitRow,
  CockpitEmpty,
} from "@/components/cockpit/CockpitShell";
import { PROJECTS_SERVER_PAGING_ENABLED } from "@/lib/dataRuntimeFlags";

const ALL_STATUSES: QuoteStatus[] = ["Lead", "Quote Created", "Appointment Scheduled", "Appointment Completed", "Follow-Up Needed", "Closed Won", "Closed Lost"];
const STATUS_DOT: Record<QuoteStatus, string> = {
  "Lead": "bg-info",
  "Quote Created": "bg-primary",
  "Appointment Scheduled": "bg-warning",
  "Appointment Completed": "bg-primary",
  "Follow-Up Needed": "bg-warning",
  "Closed Won": "bg-success",
  "Closed Lost": "bg-destructive",
};
const DISPOSITION_REASONS = [
  "Price too high", "Need to think about it", "Comparing competitors",
  "No decision maker present", "Timing not right", "Project postponed",
  "Financing concerns", "Other",
] as const;
const LEAD_SOURCES = [
  "Google Ads", "Facebook Ads", "Referral", "Home Show",
  "Inbound Call", "Website", "Repeat Customer", "Manual",
] as const;
const GLOBAL_ROLES = ["super_admin", "admin", "owner", "ops_manager", "cfo", "finance_manager", "it_staff"];

const TABS = [
  { value: "all", label: "All" },
  { value: "open", label: "Open" },
  { value: "follow_up", label: "Follow-up" },
  { value: "won", label: "Won" },
  { value: "lost", label: "Lost" },
];

interface QuotesListCockpitProps {
  embedded?: boolean;
  jobsOverride?: Job[];
  opportunitiesOverride?: Quote[];
  opportunitiesLoading?: boolean;
}

export default function QuotesListCockpit({
  embedded,
  jobsOverride,
  opportunitiesOverride,
  opportunitiesLoading = false,
}: QuotesListCockpitProps = {}) {
  const { jobs: appJobs } = useAppData();
  const jobs = jobsOverride ?? appJobs;
  const { role } = useRole();
  const { repName } = useCurrentRep();
  const { profile } = useCurrentUser();
  const navigate = useNavigate();
  const actionPerms = useActionPermissions();

  const [tab, setTab] = useState("all");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [repFilter, setRepFilter] = useState<string>("all");
  const [dispositionFilter, setDispositionFilter] = useState<string>("all");
  const [dateRange, setDateRange] = useState<string>("all");
  const [createOpen, setCreateOpen] = useState(false);
  const [newFirstName, setNewFirstName] = useState("");
  const [newLastName, setNewLastName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [newAddress, setNewAddress] = useState("");
  const [newCity, setNewCity] = useState("");
  const [newState, setNewState] = useState("FL");
  const [newZip, setNewZip] = useState("");
  const [newProjectType, setNewProjectType] = useState<ProjectType>("Shower Remodel");
  const [newLeadSource, setNewLeadSource] = useState<string>("Manual");
  const [newAssignedRep, setNewAssignedRep] = useState<string>("");
  const [newEstimate, setNewEstimate] = useState<string>("");
  const [newNotes, setNewNotes] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [repNameById, setRepNameById] = useState<Map<string, string>>(new Map());
  const [quotesData, setQuotesData] = useState<Quote[]>([]);
  const [locallyCreatedQuotes, setLocallyCreatedQuotes] = useState<Quote[]>([]);
  const [quotesLoading, setQuotesLoading] = useState(true);
  const hasExternalOpportunities = Array.isArray(opportunitiesOverride);
  const serverPaginationEnabled = PROJECTS_SERVER_PAGING_ENABLED && !!embedded;
  const [serverPage, setServerPage] = useState(0);
  const [serverPageSize, setServerPageSize] = useState(25);
  const serverRepFilter = role === "sales"
    ? String(repName ?? profile?.id ?? "all")
    : repFilter;
  const serverOpportunities = useProjectOpportunitiesPage({
    enabled: serverPaginationEnabled,
    tab: tab as any,
    page: serverPage,
    pageSize: serverPageSize,
    search,
    status: statusFilter,
    rep: serverRepFilter,
    disposition: dispositionFilter,
    dateRange,
  });
  const serverSummary = useProjectOpportunitiesSummary({
    enabled: serverPaginationEnabled,
    rep: role === "sales" ? serverRepFilter : "all",
  });

  const isUuidLike = (value: string) =>
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value.trim());

  useEffect(() => {
    if (serverPaginationEnabled || hasExternalOpportunities) {
      setQuotesLoading(false);
      return;
    }
    let cancelled = false;
    const loadQuotes = async () => {
      setQuotesLoading(true);
      const { data, error } = await QuoteService.getAll();
      if (error) {
        console.error("Failed to load quotes:", error);
        if (!cancelled) setQuotesLoading(false);
        return;
      }
      if (cancelled) return;
      const mapped = ((data ?? []) as any[]).map((row) => toUiQuote(row));
      setQuotesData(mapped);
      setQuotesLoading(false);
    };
    void loadQuotes();
    return () => {
      cancelled = true;
    };
  }, [hasExternalOpportunities, serverPaginationEnabled]);

  const sourceQuotes = useMemo(() => {
    if (!hasExternalOpportunities) return quotesData;
    const externalRows = opportunitiesOverride ?? [];
    const externalIds = new Set(externalRows.map((q) => q.id));
    return [
      ...locallyCreatedQuotes.filter((q) => !externalIds.has(q.id)),
      ...externalRows,
    ];
  }, [hasExternalOpportunities, locallyCreatedQuotes, opportunitiesOverride, quotesData]);

  const effectiveQuotesLoading = serverPaginationEnabled
    ? serverOpportunities.loading
    : hasExternalOpportunities
      ? opportunitiesLoading
      : quotesLoading;

  const visibleQuotes = useMemo(
    () => {
      if (serverPaginationEnabled) return serverOpportunities.opportunities;
      const shouldScopeToOwnRep = role === "sales";
      if (!shouldScopeToOwnRep) return sourceQuotes;
      const normalizedRepName = String(repName ?? "").trim().toLowerCase();
      const profileId = String(profile?.id ?? "").trim();
      return sourceQuotes.filter((q) => {
        const assigned = String(q.assignedRep ?? "").trim();
        const createdBy = String(q.createdBy ?? "").trim();
        if (profileId && (assigned === profileId || createdBy === profileId)) return true;
        const repLabel = getRepLabel(q).toLowerCase();
        return normalizedRepName.length > 0 && repLabel === normalizedRepName;
      });
    },
    [sourceQuotes, role, repName, profile?.id, repNameById, serverPaginationEnabled, serverOpportunities.opportunities]
  );

  useEffect(() => {
    let cancelled = false;
    const loadRepNames = async () => {
      const repIds = Array.from(
        new Set(
          sourceQuotes
            .flatMap((q) => [q.assignedRep, q.createdBy])
            .filter((v): v is string => typeof v === "string" && isUuidLike(v)),
        ),
      );
      if (repIds.length === 0) {
        setRepNameById(new Map());
        return;
      }
      const [byIdRes, byAuthIdRes] = await Promise.all([
        supabase
          .from("profiles")
          .select("id, auth_user_id, full_name, first_name, last_name")
          .in("id", repIds),
        supabase
          .from("profiles")
          .select("id, auth_user_id, full_name, first_name, last_name")
          .in("auth_user_id", repIds),
      ]);

      if (cancelled) return;

      const map = new Map<string, string>();
      const rows = [
        ...((byIdRes.data ?? []) as Array<Record<string, unknown>>),
        ...((byAuthIdRes.data ?? []) as Array<Record<string, unknown>>),
      ];
      for (const row of rows) {
        const first = String(row.first_name ?? "").trim();
        const last = String(row.last_name ?? "").trim();
        const fullName = String(row.full_name ?? "").trim() || [first, last].filter(Boolean).join(" ").trim();
        const id = String(row.id ?? "").trim();
        const authUserId = String(row.auth_user_id ?? "").trim();
        if (fullName) {
          if (id) map.set(id, fullName);
          if (authUserId) map.set(authUserId, fullName);
        }
      }
      setRepNameById(map);
    };
    void loadRepNames();
    return () => {
      cancelled = true;
    };
  }, [sourceQuotes]);

  function getRepLabel(q: Quote) {
    const assigned = (q.assignedRep || "").trim();
    if (assigned && !isUuidLike(assigned)) return assigned;
    if (assigned && isUuidLike(assigned)) return repNameById.get(assigned) ?? "—";
    const created = (q.createdBy || "").trim();
    if (created && isUuidLike(created)) return repNameById.get(created) ?? "—";
    if (created) return created;
    return "—";
  }

  const allReps = useMemo(
    () => Array.from(new Set(sourceQuotes.map(getRepLabel).filter((v) => v && v !== "—"))).sort(),
    [sourceQuotes, repNameById]
  );

  const isInDateRange = (dateStr: string) => {
    if (dateRange === "all") return true;
    const diffDays = (Date.now() - new Date(dateStr).getTime()) / 86_400_000;
    if (dateRange === "7") return diffDays <= 7;
    if (dateRange === "14") return diffDays <= 14;
    if (dateRange === "30") return diffDays <= 30;
    if (dateRange === "90") return diffDays <= 90;
    return true;
  };

  const filtered = useMemo(() => {
    if (serverPaginationEnabled) return visibleQuotes;
    return visibleQuotes.filter(q => {
      if (tab === "open" && !isActiveStatus(q.stage)) return false;
      if (tab === "follow_up" && q.stage !== "Follow-Up Needed") return false;
      if (tab === "won" && q.stage !== "Closed Won") return false;
      if (tab === "lost" && q.stage !== "Closed Lost") return false;
      const matchSearch = !search ||
        q.customerName.toLowerCase().includes(search.toLowerCase()) ||
        (q.displayReference || q.id).toLowerCase().includes(search.toLowerCase()) ||
        getRepLabel(q).toLowerCase().includes(search.toLowerCase());
      const matchStatus = statusFilter === "all" || q.stage === statusFilter;
      const matchRep = repFilter === "all" || getRepLabel(q) === repFilter;
      const matchDisposition = dispositionFilter === "all" || q.lostReason === dispositionFilter;
      const matchDate = isInDateRange(q.createdAt);
      return matchSearch && matchStatus && matchRep && matchDisposition && matchDate;
    });
  }, [visibleQuotes, tab, search, statusFilter, repFilter, dispositionFilter, dateRange, repNameById, serverPaginationEnabled]);
  const localPagination = usePagination(filtered, 25);
  useEffect(() => {
    if (serverPaginationEnabled) {
      setServerPage(0);
      return;
    }
    localPagination.reset();
  }, [search, tab, statusFilter, repFilter, dispositionFilter, dateRange, serverPaginationEnabled]);
  const serverTotalPages = Math.max(1, Math.ceil(serverOpportunities.count / serverPageSize));
  const safeServerPage = Math.min(serverPage, serverTotalPages - 1);
  useEffect(() => {
    if (serverPaginationEnabled && serverPage !== safeServerPage) {
      setServerPage(safeServerPage);
    }
  }, [safeServerPage, serverPage, serverPaginationEnabled]);
  const pagination = serverPaginationEnabled
    ? {
        paged: filtered,
        page: safeServerPage,
        totalPages: serverTotalPages,
        totalItems: serverOpportunities.count,
        hasNext: safeServerPage < serverTotalPages - 1,
        hasPrev: safeServerPage > 0,
        next: () => setServerPage((p) => Math.min(p + 1, serverTotalPages - 1)),
        prev: () => setServerPage((p) => Math.max(p - 1, 0)),
        reset: () => setServerPage(0),
        pageStart: serverOpportunities.count > 0 ? safeServerPage * serverPageSize + 1 : 0,
        pageEnd: Math.min((safeServerPage + 1) * serverPageSize, serverOpportunities.count),
        pageSize: serverPageSize,
        setPageSize: (size: number) => {
          setServerPageSize(size);
          setServerPage(0);
        },
      }
    : localPagination;

  const getProjectTypeLabel = (q: Quote) =>
    q.projectType ||
    q.projectConfig?.projectCategory ||
    q.projectConfig?.consultationCategory ||
    "Other";

  const getDispositionLabel = (q: Quote) =>
    q.lostReason ||
    q.dispositionReason ||
    q.wonOutcome ||
    "—";

  const getRowRoute = (q: Quote) => {
    const isUuidLike = (value: string) =>
      /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value.trim());

    if (q.stage === "Closed Won") {
      const jobRef = (q.linkedJobId || q.convertedJobId || (q as { jobId?: string }).jobId || "").trim();
      if (jobRef) {
        if (isUuidLike(jobRef)) return `/jobs/${jobRef}`;
        const matched = jobs.find((j) =>
          j.id === jobRef ||
          String((j as { jobNumber?: string }).jobNumber ?? "").trim() === jobRef,
        );
        if (matched?.id) return `/jobs/${matched.id}`;
      }
    }
    return `/opportunities/${q.id}`;
  };

  const metrics = useMemo(() => {
    if (serverPaginationEnabled && serverSummary.data) {
      return {
        totalOpen: serverSummary.data.open,
        totalFollowUp: serverSummary.data.followUp,
        aging14: serverSummary.data.aging14,
        closeRate: serverSummary.data.closeRate,
        pipelineValue: serverSummary.data.pipelineValue,
      };
    }
    const enriched = getAllOpportunities(visibleQuotes);
    const stats = getOpportunityStats(enriched, jobs);
    return {
      totalOpen: stats.openCount,
      totalFollowUp: stats.followUpCount,
      aging14: stats.aging14,
      closeRate: stats.closeRate,
      pipelineValue: stats.pipelineValue,
    };
  }, [visibleQuotes, jobs, serverPaginationEnabled, serverSummary.data]);

  const showRepColumn = GLOBAL_ROLES.includes(role);

  const tabCounts = useMemo(() => ({
    all: serverPaginationEnabled && serverSummary.data ? serverSummary.data.all : visibleQuotes.length,
    open: serverPaginationEnabled && serverSummary.data ? serverSummary.data.open : visibleQuotes.filter(q => isActiveStatus(q.stage)).length,
    follow_up: serverPaginationEnabled && serverSummary.data ? serverSummary.data.followUp : visibleQuotes.filter(q => q.stage === "Follow-Up Needed").length,
    won: serverPaginationEnabled && serverSummary.data ? serverSummary.data.won : visibleQuotes.filter(q => q.stage === "Closed Won").length,
    lost: serverPaginationEnabled && serverSummary.data ? serverSummary.data.lost : visibleQuotes.filter(q => q.stage === "Closed Lost").length,
  }), [visibleQuotes, serverPaginationEnabled, serverSummary.data]);

  const resetForm = () => {
    setNewFirstName(""); setNewLastName("");
    setNewEmail(""); setNewPhone("");
    setNewAddress(""); setNewCity(""); setNewState("FL"); setNewZip("");
    setNewProjectType("Shower Remodel");
    setNewLeadSource("Manual");
    setNewAssignedRep("");
    setNewEstimate("");
    setNewNotes("");
  };

  const isFormValid = !!(
    newFirstName.trim() &&
    newLastName.trim() &&
    (newPhone.trim() || newEmail.trim()) &&
    newProjectType &&
    newLeadSource &&
    (role === "sales" || newAssignedRep)
  );

  const resolveAssignedRepId = async (repName: string): Promise<string | undefined> => {
    if (!repName.trim()) return undefined;
    const { data: profiles } = await supabase
      .from("profiles")
      .select("id, full_name, first_name, last_name");
    const needle = repName.trim().toLowerCase();
    for (const p of (profiles ?? []) as Array<Record<string, unknown>>) {
      const fullName = String(p.full_name ?? "").trim();
      const composed = [String(p.first_name ?? "").trim(), String(p.last_name ?? "").trim()].filter(Boolean).join(" ").trim();
      const candidate = (fullName || composed).toLowerCase();
      if (candidate && candidate === needle) return String(p.id ?? "");
    }
    return undefined;
  };

  const extractCreateResultIds = (data: unknown): { customerId?: string; leadId?: string } => {
    const row = Array.isArray(data) ? data[0] : data;
    if (!row || typeof row !== "object") return {};
    const r = row as Record<string, unknown>;
    return {
      customerId: String(r.customer_id ?? r.customerId ?? "").trim() || undefined,
      leadId: String(r.lead_id ?? r.leadId ?? r.id ?? "").trim() || undefined,
    };
  };

  const handleCreate = async (mode: "open" | "another") => {
    if (!isFormValid || isSaving) return;
    setIsSaving(true);
    try {
      const fullName = `${newFirstName.trim()} ${newLastName.trim()}`.trim();
      const estimate = Math.max(0, parseFloat(newEstimate.replace(/[^0-9.]/g, "")) || 0);
      const assignedRepName = role === "sales" ? String(repName || "").trim() : newAssignedRep.trim();
      const assignedRepId = await resolveAssignedRepId(assignedRepName);

      const leadCreate = await LeadService.createWithCustomer({
        primary_name: fullName,
        primary_phone: newPhone.trim(),
        primary_email: newEmail.trim() || "",
        address: newAddress.trim(),
        city: newCity.trim(),
        state: newState.trim() || "FL",
        zip: newZip.trim(),
        project_type: newProjectType,
        lead_source: newLeadSource,
        assigned_rep: assignedRepId,
        notes: newNotes.trim() || null,
      });
      if (leadCreate.error || !leadCreate.data) {
        throw new Error((leadCreate.error as { message?: string } | null)?.message || "Failed to create lead/customer");
      }

      const { customerId, leadId } = extractCreateResultIds(leadCreate.data);
      if (!customerId || !leadId) {
        throw new Error("Lead/customer creation returned incomplete IDs");
      }

      const quoteCreate = await QuoteService.create({
        quote_name: fullName,
        customer_name: fullName,
        customer_email: newEmail.trim() || null,
        customer_phone: newPhone.trim() || null,
        customer_address: [newAddress.trim(), newCity.trim(), newState.trim(), newZip.trim()].filter(Boolean).join(", ") || null,
        lead_source: newLeadSource || null,
        status: "Lead",
        notes: newNotes.trim() || null,
        total_estimate: estimate,
        customer_id: customerId,
        lead_id: leadId,
      });
      if (quoteCreate.error || !quoteCreate.data) {
        throw new Error((quoteCreate.error as { message?: string } | null)?.message || "Failed to create quote");
      }

      const created = quoteCreate.data as Record<string, unknown>;
      const createdId = String(created.id ?? "").trim();
      const mappedQuote = toUiQuote(created as any);
      if (hasExternalOpportunities) {
        setLocallyCreatedQuotes((prev) => [mappedQuote as Quote, ...prev]);
      } else {
        setQuotesData((prev) => [mappedQuote as Quote, ...prev]);
      }

      if (mode === "open") {
        setCreateOpen(false);
        resetForm();
        navigate(`/opportunities/${createdId}`);
      } else {
        const displayReference = String(created.quote_number ?? created.display_reference ?? createdId).trim();
        toast.success(`Opportunity ${displayReference || createdId} created`, { description: `${mappedQuote.primaryName} · ${mappedQuote.projectType}` });
        setNewFirstName(""); setNewLastName("");
        setNewEmail(""); setNewPhone("");
        setNewAddress(""); setNewCity(""); setNewZip("");
        setNewEstimate(""); setNewNotes("");
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to create opportunity";
      toast.error("Create failed", { description: message });
    } finally {
      setIsSaving(false);
    }
  };

  const filtersActive = !!(search || statusFilter !== "all" || repFilter !== "all" || dispositionFilter !== "all" || dateRange !== "all");

  const template = showRepColumn
    ? "0.7fr 1.6fr 1.1fr 1.1fr 1.2fr 1.2fr 1fr 0.9fr auto"
    : "0.7fr 1.6fr 1.1fr 1.2fr 1.2fr 1fr 0.9fr auto";

  const body = (
    <>
      <section className="space-y-4">
        <div className="flex items-center justify-between gap-4">
          <CockpitTabs
            value={tab}
            onChange={setTab}
            tabs={TABS.map(t => ({ ...t, count: tabCounts[t.value as keyof typeof tabCounts] }))}
          />
          <Button onClick={() => setCreateOpen(true)} size="sm" className="gap-1.5 shrink-0" {...actionPerms.guardProps("create_opportunity")}>
            <Plus className="h-3.5 w-3.5" /> New
          </Button>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <CockpitSearch value={search} onChange={setSearch} placeholder="Search customer, ID, or rep" />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-9 w-44 text-sm bg-transparent border-border/60"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all" className="text-sm">All statuses</SelectItem>
              {ALL_STATUSES.map(s => <SelectItem key={s} value={s} className="text-sm">{s}</SelectItem>)}
            </SelectContent>
          </Select>
          {showRepColumn && (
            <Select value={repFilter} onValueChange={setRepFilter}>
              <SelectTrigger className="h-9 w-40 text-sm bg-transparent border-border/60"><SelectValue placeholder="Rep" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all" className="text-sm">All reps</SelectItem>
                {allReps.map(r => <SelectItem key={r} value={r} className="text-sm">{r}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Select value={dispositionFilter} onValueChange={setDispositionFilter}>
            <SelectTrigger className="h-9 w-44 text-sm bg-transparent border-border/60"><SelectValue placeholder="Disposition" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all" className="text-sm">All dispositions</SelectItem>
              {DISPOSITION_REASONS.map(r => <SelectItem key={r} value={r} className="text-sm">{r}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="h-9 w-32 text-sm bg-transparent border-border/60"><SelectValue placeholder="Date" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all" className="text-sm">All time</SelectItem>
              <SelectItem value="7" className="text-sm">Last 7 days</SelectItem>
              <SelectItem value="14" className="text-sm">Last 14 days</SelectItem>
              <SelectItem value="30" className="text-sm">Last 30 days</SelectItem>
              <SelectItem value="90" className="text-sm">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          {filtersActive && (
            <Button
              variant="ghost" size="sm"
              className="h-9 text-sm text-muted-foreground hover:text-foreground"
              onClick={() => { setSearch(""); setStatusFilter("all"); setRepFilter("all"); setDispositionFilter("all"); setDateRange("all"); }}
            >Clear</Button>
          )}
        </div>
      </section>

      <section>
        {effectiveQuotesLoading ? (
          <CockpitEmpty message="Loading latest opportunities..." />
        ) : filtered.length === 0 ? (
          <CockpitEmpty message="No opportunities match the current filters." />
        ) : (
          <CockpitListShell>
            <CockpitColumns template={template}>
              <span>ID</span>
              <span>Customer</span>
              {showRepColumn && <span>Rep</span>}
              <span>Type</span>
              <span>Status</span>
              <span>Disposition</span>
              <span className="text-right">Total</span>
              <span>Created</span>
              <span />
            </CockpitColumns>
            {pagination.paged.map(q => {
              const ageDays = Math.floor((Date.now() - new Date(q.createdAt).getTime()) / 86_400_000);
              const aging = isActiveStatus(q.stage) && ageDays > 14;
              const projectType = getProjectTypeLabel(q);
              const disposition = getDispositionLabel(q);
              return (
                <CockpitRow key={q.id} to={getRowRoute(q)} template={template}>
                  <span className="text-sm font-medium text-foreground tabular-nums">{q.displayReference || q.id}</span>
                  <div className="min-w-0">
                    <p className="text-sm text-foreground font-medium truncate">{q.customerName}</p>
                    {aging && <p className="text-[11px] text-warning mt-0.5">Aging {ageDays}d</p>}
                  </div>
                  {showRepColumn && <span className="text-sm text-foreground/90 truncate">{getRepLabel(q)}</span>}
                  <span className="text-sm text-foreground/80 truncate">{projectType}</span>
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={cn("h-1.5 w-1.5 rounded-full shrink-0", STATUS_DOT[q.stage])} />
                    <span className="text-sm text-foreground/90 truncate">{q.stage}</span>
                  </div>
                  <span className="text-sm text-muted-foreground truncate">{disposition}</span>
                  <span className="text-sm tabular-nums text-foreground font-medium text-right">
                    {q.totalEstimate > 0 ? `$${q.totalEstimate.toLocaleString()}` : "—"}
                  </span>
                  <span className="text-sm text-muted-foreground tabular-nums">{formatShortDate(q.createdAt)}</span>
                </CockpitRow>
              );
            })}
          </CockpitListShell>
        )}
        <PaginationBar {...pagination} />
        <p className="text-[11px] text-muted-foreground text-right mt-3 tabular-nums">
          {serverPaginationEnabled
            ? `${serverOpportunities.count} of ${tabCounts[tab as keyof typeof tabCounts] ?? serverOpportunities.count}`
            : `${filtered.length} of ${visibleQuotes.length}`}
        </p>
      </section>

      <Dialog open={createOpen} onOpenChange={(o) => { setCreateOpen(o); if (!o) resetForm(); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>New opportunity</DialogTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Captures contact, project, source, and assignment so the opportunity is operational on save.
            </p>
          </DialogHeader>

          <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
            {/* Customer */}
            <div>
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-2">Customer</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">First name *</Label>
                  <Input value={newFirstName} onChange={e => setNewFirstName(e.target.value)} placeholder="Jane" className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs">Last name *</Label>
                  <Input value={newLastName} onChange={e => setNewLastName(e.target.value)} placeholder="Smith" className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs">Phone {!newEmail.trim() && "*"}</Label>
                  <Input value={newPhone} onChange={e => setNewPhone(formatPhoneNumber(e.target.value))} placeholder="555-000-0000" className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs">Email {!newPhone.trim() && "*"}</Label>
                  <Input type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="email@example.com" className="mt-1" />
                </div>
              </div>
              <p className="text-[10px] text-muted-foreground mt-1.5">At least one contact channel required.</p>
            </div>

            {/* Project */}
            <div>
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-2">Project</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Project type *</Label>
                  <Select value={newProjectType} onValueChange={(v) => setNewProjectType(v as ProjectType)}>
                    <SelectTrigger className="mt-1 h-9 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PROJECT_TYPES.map(t => <SelectItem key={t} value={t} className="text-sm">{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Estimated value</Label>
                  <Input
                    inputMode="decimal"
                    value={newEstimate}
                    onChange={e => setNewEstimate(e.target.value)}
                    placeholder="$ optional"
                    className="mt-1"
                  />
                </div>
              </div>
            </div>

            {/* Routing */}
            <div>
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-2">Source &amp; assignment</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Lead source *</Label>
                  <Select value={newLeadSource} onValueChange={setNewLeadSource}>
                    <SelectTrigger className="mt-1 h-9 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {LEAD_SOURCES.map(s => <SelectItem key={s} value={s} className="text-sm">{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Assigned rep {role !== "sales" && "*"}</Label>
                  {role === "sales" ? (
                    <Input value={String(repName || "").trim() || "Assigned to signed-in rep"} disabled className="mt-1" />
                  ) : (
                    <Select value={newAssignedRep} onValueChange={setNewAssignedRep}>
                      <SelectTrigger className="mt-1 h-9 text-sm"><SelectValue placeholder="Select rep" /></SelectTrigger>
                      <SelectContent>
                        {allReps.length === 0 && <SelectItem value="Current User" className="text-sm">Current User</SelectItem>}
                        {allReps.map(r => <SelectItem key={r} value={r} className="text-sm">{r}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </div>
            </div>

            {/* Address */}
            <div>
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-2">Address <span className="text-muted-foreground/70 normal-case">(optional)</span></p>
              <div className="space-y-2">
                <Input value={newAddress} onChange={e => setNewAddress(e.target.value)} placeholder="Street address" />
                <div className="grid grid-cols-3 gap-2">
                  <Input value={newCity} onChange={e => setNewCity(e.target.value)} placeholder="City" />
                  <Input value={newState} onChange={e => setNewState(e.target.value.toUpperCase().slice(0, 2))} placeholder="State" maxLength={2} />
                  <Input value={newZip} onChange={e => setNewZip(e.target.value.replace(/[^0-9-]/g, "").slice(0, 10))} placeholder="ZIP" />
                </div>
              </div>
            </div>

            {/* Notes */}
            <div>
              <Label className="text-xs">Notes</Label>
              <Textarea value={newNotes} onChange={e => setNewNotes(e.target.value)} placeholder="Initial notes…" className="mt-1" rows={2} />
            </div>
          </div>

          <DialogFooter className="flex items-center justify-between sm:justify-between gap-2">
            <Button variant="ghost" onClick={() => { setCreateOpen(false); resetForm(); }}>Cancel</Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => void handleCreate("another")} disabled={!isFormValid || isSaving}>
                Save &amp; new
              </Button>
              <Button onClick={() => void handleCreate("open")} disabled={!isFormValid || isSaving}>
                Save &amp; open
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );

  if (embedded) return <div className="flex flex-col gap-4 pt-2">{body}</div>;

  return (
    <CockpitPage>
      <CockpitMasthead
        title="Opportunities"
        right={
          <>
            <HeroMetric
              label="Pipeline value"
              value={`$${(metrics.pipelineValue / 1000).toFixed(metrics.pipelineValue >= 100_000 ? 0 : 1)}k`}
              hero
            />
            <HeroDivider />
            <HeroMetric label="Open" value={metrics.totalOpen} />
            <HeroMetric label="Follow-up" value={metrics.totalFollowUp} />
            <HeroMetric label="Aging > 14d" value={metrics.aging14} tone={metrics.aging14 > 0 ? "warn" : "default"} />
            <HeroMetric label="Close rate" value={`${metrics.closeRate}%`} />
          </>
        }
      />
      {body}
    </CockpitPage>
  );
}
